/**
 * Opportunities in the partner portal (flag crm.opportunities; the API answers 404 feature_disabled while it is off, and these
 * screens and their menu entries then do not appear). Two directions, both built from features/opportunities:
 *  /opportunities, /opportunities/:id, /opportunities/staff   the agency's own sales desk: quotations to its client workspaces,
 *                                                              its sales staff, reporting line, limits and head of operations
 *  /opportunities/purchases, /opportunities/purchases/:id      the agency buying from Quixo, like a customer does
 */
import { useMemo, type ReactNode } from 'react';
import { BffError } from '../../../lib/bff';
import { OpportunitiesProvider, type OppEnv } from '../../../features/opportunities/env';
import { BuyerDetailScreen, BuyerListScreen } from '../../../features/opportunities/Buyer';
import { DeskDetailScreen, DeskQueueScreen, SalesStaffScreen } from '../../../features/opportunities/Desk';
import { useSurface } from '../kit/context';
import { useResource } from '../kit/resource';
import { Notice, Screen } from '../kit/ui';
import { usePartnerApi } from './layout';

const MANAGERS: readonly string[] = ['partner_owner', 'partner_admin'];
const isManager = (roles: readonly string[]) => roles.some((r) => MANAGERS.includes(r));
const isMember = (roles: readonly string[]) => roles.some((r) => r === 'partner_member' || MANAGERS.includes(r));

export function useOpportunitiesEnv(variant: 'agency' | 'purchase'): OppEnv {
  const { t, locale, roles, http } = useSurface();
  return useMemo<OppEnv>(() => ({
    variant, t, locale, roles,
    http: { get: (p) => http.get(p), send: (method, p, body) => (method === 'POST' ? http.post(p, body) : http.put(p, body)) },
    failure: (e) => (e instanceof BffError
      ? { status: e.status, reason: (e.envelope?.error.details as { reason?: string } | undefined)?.reason, message: e.envelope?.error.message }
      : { status: 0 }),
    apiBase: variant === 'agency' ? '/v1/opportunities/agency' : '/v1/opportunities/agency/purchases',
    links: variant === 'agency'
      ? { list: '/opportunities', detail: (id) => `/opportunities/${encodeURIComponent(id)}`, staff: '/opportunities/staff' }
      : { list: '/opportunities/purchases', detail: (id) => `/opportunities/purchases/${encodeURIComponent(id)}`, staff: '/opportunities/staff' },
    Screen,
    // the API enforces the same: members sell with an active sales profile; owners and admins manage staff, assign and act for the account manager
    can: { sell: isMember, assign: isManager, manage: isManager, actAs: isManager },
  }), [variant, t, locale, roles, http]);
}

/** Renders the children only when the agency opportunities API answers (flag on, approved agency). */
function Available({ variant, children }: { variant: 'agency' | 'purchase'; children: ReactNode }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const me = useResource('opportunities:agency', api.opportunitiesMe);
  const env = useOpportunitiesEnv(variant);
  if (me.status === 'loading') return <Screen title={t('opportunities.agency.title')}><p>{t('portal.kit.loading')}</p></Screen>;
  if (me.status === 'error') return <Screen title={t('opportunities.agency.title')} testId="opportunities-unavailable"><Notice>{t('opportunities.agency.unavailable')}</Notice></Screen>;
  return <OpportunitiesProvider env={env}>{children}</OpportunitiesProvider>;
}

export function AgencyQueuePage() { return <Available variant="agency"><DeskQueueScreen /></Available>; }
export function AgencyOpportunityPage() { return <Available variant="agency"><DeskDetailScreen /></Available>; }
export function AgencyStaffPage() { return <Available variant="agency"><SalesStaffScreen /></Available>; }
export function PurchasesPage() { return <Available variant="purchase"><BuyerListScreen /></Available>; }
export function PurchasePage() { return <Available variant="purchase"><BuyerDetailScreen /></Available>; }
